import moment from 'moment';

/**
 * @function
 * @description Compares two arrays for deep equality by checking length and JSON stringifying each element.
 * Performs a deep comparison of array elements using JSON.stringify.
 *
 * @param {Array} arr1 - First array to compare
 * @param {Array} arr2 - Second array to compare
 * @returns {boolean} True if arrays are equal, false otherwise
 *
 * @example
 * const arr1 = [{id: 1, name: 'John'}, {id: 2, name: 'Jane'}];
 * const arr2 = [{id: 1, name: 'John'}, {id: 2, name: 'Jane'}];
 * const result = isEqualsArray(arr1, arr2); // true
 *
 * @throws {TypeError} Returns false if inputs are not arrays
 */
export const isEqualsArray = (arr1, arr2) => {
    if (!(arr1 instanceof Array) || !(arr1 instanceof Array)) return false;

    if (arr1.length !== arr2.length) return false;

    if (arr1 instanceof Object && arr2 instanceof Object) {
        let isEqual = true,
            arrayLength = arr1.length;
        for (let index = 0; index < arrayLength && isEqual; index++) {
            if (JSON.stringify(arr1[index]) !== JSON.stringify(arr2[index])) isEqual = false;
        }

        return isEqual;
    }

    return false;
};

/**
 * @function
 * @description Checks if an object is empty (has no enumerable properties).
 *
 * @param {Object} obj - Object to check for emptiness
 * @returns {boolean} True if object is empty, false if it has properties
 *
 * @example
 * const emptyObj = {};
 * const nonEmptyObj = {name: 'John'};
 * isEmpty(emptyObj); // true
 * isEmpty(nonEmptyObj); // false
 */
export function isEmpty(obj) {
    for (var key in obj) {
        if (Object.prototype.hasOwnProperty.call(obj, key)) {
            return false;
        }
    }
    return true;
}

/**
 * @function
 * @description Detects if the current browser is Internet Explorer (IE).
 * Checks user agent string for IE-specific identifiers.
 *
 * @returns {boolean} True if browser is IE, false otherwise
 *
 * @example
 * if (isIEBrowser()) {
 *   console.log('Running on Internet Explorer');
 * }
 */
export function isIEBrowser() {
    // BROWSER CHECK VARIABLES
    const ua = window.navigator.userAgent;
    const msie = ua.indexOf('MSIE ');
    const msie11 = ua.indexOf('Trident/');
    // const msedge = ua.indexOf('Edge/');
    return msie > 0 || msie11 > 0;
    // const isEdge = msedge > 0;
}

/**
 * @constant
 * @description Regular expression for validating email addresses.
 * Supports international email formats with length validation (6-254 characters).
 *
 * @type {RegExp}
 *
 * @example
 * const email = 'user@example.com';
 * const isValid = VALIDATE_EMAIL.test(email); // true
 */
export const VALIDATE_EMAIL =
    /^(?=[a-zA-Z0-9@._%+-]{6,254}$)[a-zA-Z0-9](?:[a-zA-Z0-9._%+-]*[a-zA-Z0-9])?@(?:[a-zA-Z0-9-]{1,63}\.){1,8}[a-zA-Z]{2,63}$/;

/**
 * @function
 * @description Prints a DOM element to PDF by opening a new window with the element's content.
 * Creates a new window with basic styling and triggers the browser's print dialog.
 *
 * @param {HTMLElement} div - DOM element to print
 * @returns {void}
 *
 * @example
 * const element = document.getElementById('report-content');
 * printDivToPDF(element);
 *
 * @throws {Error} Returns early if div is null or print window cannot be opened
 */
export function printDivToPDF(div) {
    if (!div) return;

    const printWindow = window.open('', '_blank');
    if (!printWindow) return;

    printWindow.document.write(`
      <html>
        <head>
          <title></title>
          <style>
            body {
              font-family: sans-serif;
              padding: 20px;
            }
          </style>
        </head>
        <body>
          ${div.outerHTML}
        </body>
      </html>
    `);

    printWindow.document.close();

    printWindow.focus();
    printWindow.print();
    printWindow.close(); // optional
}

/**
 * @function
 * @description Calculates the end date and time by adding duration to start time on a given date.
 * Handles time calculations across date boundaries and formats the result in a readable format.
 *
 * @param {string} startTime - Start time in HH:MM:SS format
 * @param {string} duration - Duration to add in HH:MM:SS format
 * @param {string} eventDate - Event date in YYYY-MM-DD format
 * @returns {string} Formatted end date and time string (e.g., "15 Jan 2024 02:30:00 PM")
 *
 * @example
 * const endTime = calculateEndDateTime('10:00:00', '04:30:00', '2024-01-15');
 * console.log(endTime); // "15 Jan 2024 02:30:00 PM"
 */
export function calculateEndDateTime(startTime, duration, eventDate) {
    const [startHours, startMinutes, startSeconds] = startTime.split(':').map(Number);
    const [durationHours, durationMinutes, durationSeconds] = duration.split(':').map(Number);
    const [year, month, day] = eventDate.split('-').map(Number); // YYYY-MM-DD

    // Initialize with event date + start time
    const startDate = new Date(year, month - 1, day, startHours, startMinutes, startSeconds);

    // Add duration in milliseconds
    const endDate = new Date(
        startDate.getTime() +
            durationHours * 3600000 +
            durationMinutes * 60000 +
            durationSeconds * 1000
    );

    // Format date
    const dayStr = String(endDate.getDate()).padStart(2, '0');
    const monthNames = [
        'Jan',
        'Feb',
        'Mar',
        'Apr',
        'May',
        'Jun',
        'Jul',
        'Aug',
        'Sep',
        'Oct',
        'Nov',
        'Dec'
    ];
    const monthStr = monthNames[endDate.getMonth()];
    const yearStr = endDate.getFullYear();

    // Format time with AM/PM
    let hours = endDate.getHours();
    const minutes = String(endDate.getMinutes()).padStart(2, '0');
    const seconds = String(endDate.getSeconds()).padStart(2, '0');
    const ampm = hours >= 12 ? 'PM' : 'AM';
    hours = hours % 12 || 12; // convert 0 to 12 for 12AM/PM
    const hourStr = String(hours).padStart(2, '0');

    return `${dayStr} ${monthStr} ${yearStr} ${hourStr}:${minutes}:${seconds} ${ampm}`;
}

/**
 * @function
 * @description Formats a start time and event date into a readable date-time string.
 * Converts 24-hour time format to 12-hour format with AM/PM.
 *
 * @param {string} startTime - Start time in HH:MM:SS format
 * @param {string} eventDate - Event date in YYYY-MM-DD format
 * @returns {string} Formatted date-time string (e.g., "15 Jan 2024 10:00:00 AM")
 *
 * @example
 * const formatted = formatEventDateTime('14:30:00', '2024-01-15');
 * console.log(formatted); // "15 Jan 2024 02:30:00 PM"
 */
export function formatEventDateTime(startTime, eventDate) {
    const [hours, minutes, seconds] = startTime.split(':').map(Number);
    const [year, month, day] = eventDate.split('-').map(Number); // YYYY-MM-DD

    const date = new Date(year, month - 1, day, hours, minutes, seconds);

    // Format date
    const dayStr = String(date.getDate()).padStart(2, '0');
    const monthNames = [
        'Jan',
        'Feb',
        'Mar',
        'Apr',
        'May',
        'Jun',
        'Jul',
        'Aug',
        'Sep',
        'Oct',
        'Nov',
        'Dec'
    ];
    const monthStr = monthNames[date.getMonth()];
    const yearStr = date.getFullYear();

    // Format time with AM/PM
    let hour = date.getHours();
    const ampm = hour >= 12 ? 'PM' : 'AM';
    hour = hour % 12 || 12; // convert 0 to 12
    const hourStr = String(hour).padStart(2, '0');
    const minuteStr = String(date.getMinutes()).padStart(2, '0');
    const secondStr = String(date.getSeconds()).padStart(2, '0');

    return `${dayStr} ${monthStr} ${yearStr} ${hourStr}:${minuteStr}:${secondStr} ${ampm}`;
}

/**
 * @function
 * @description Formats a duration string into a human-readable format.
 * Converts HH:MM:SS format to "Xhr Ymin Zsec" format.
 *
 * @param {string} duration - Duration in HH:MM:SS format
 * @returns {string} Human-readable duration string (e.g., "2hr 30min 15sec")
 *
 * @example
 * const formatted = formatDuration('02:30:15');
 * console.log(formatted); // "2hr 30min 15sec"
 */
export function formatDuration(duration) {
    const [hours, minutes, seconds] = duration.split(':').map(Number);

    const hrStr = `${hours}hr`;
    const minStr = `${minutes}min`;
    const secStr = `${seconds}sec`;

    return `${hrStr} ${minStr} ${secStr}`;
}

/**
 * @function
 * @description Formats a date into different display formats.
 * Supports two formats: short format (Jan 15) and full format (15-Jan-2024).
 *
 * @param {Date|string} date - Date object or date string to format
 * @param {boolean} [changeFormat=false] - If true, returns short format; if false, returns full format
 * @returns {string} Formatted date string
 *
 * @example
 * const date = new Date('2024-01-15');
 * getDateTimeFormat(date, true); // "Jan 15"
 * getDateTimeFormat(date, false); // "15-Jan-2024"
 */
export const getDateTimeFormat = (date, changeFormat) => {
    const months = [
        'Jan',
        'Feb',
        'Mar',
        'Apr',
        'May',
        'Jun',
        'Jul',
        'Aug',
        'Sep',
        'Oct',
        'Nov',
        'Dec'
    ];
    const dateString = new Date(date);

    return changeFormat
        ? months[dateString.getMonth()] + ' ' + dateString.getDate()
        : dateString.getDate() +
              '-' +
              months[dateString.getMonth()] +
              '-' +
              dateString.getFullYear();
};

/**
 * @function
 * @description Converts UTC date to local date-time string using moment.js.
 *
 * @param {string|Date} date - UTC date to convert
 * @returns {string} Local date-time string in "D MMM YYYY, hh:mm A" format
 *
 * @example
 * const utcDate = '2024-01-15T14:30:00Z';
 * const localTime = formatUTCToLocalDateTime(utcDate);
 * console.log(localTime); // "15 Jan 2024, 02:30 PM" (depends on timezone)
 */
export const formatUTCToLocalDateTime = (date) => {
    return moment.utc(date).local().format('D MMM YYYY, hh:mm A');
};

/**
 * @function
 * @description Formats a naive local date-time string using moment.js.
 * Assumes the input is already in local time format.
 *
 * @param {string} date - Date string in 'YYYY-MM-DDTHH:mm:ss' format
 * @returns {string} Formatted date-time string in "D MMM YYYY, hh:mm A" format
 *
 * @example
 * const naiveDate = '2024-01-15T14:30:00';
 * const formatted = formatNaiveLocalDateTimeString(naiveDate);
 * console.log(formatted); // "15 Jan 2024, 02:30 PM"
 */
export const formatNaiveLocalDateTimeString = (date) => {
    return moment(date, 'YYYY-MM-DDTHH:mm:ss').format('D MMM YYYY, hh:mm A');
};

/**
 * @constant
 * @description Regular expression for validating international postal/ZIP codes.
 * Supports US ZIP codes, Canadian postal codes, UK postcodes, and generic international formats.
 *
 * @type {RegExp}
 *
 * @example
 * const zipCode = '12345';
 * const isValid = pinCodeRegex.test(zipCode); // true
 */
export const pinCodeRegex = new RegExp(
    [
        /^[0-9]{5}(-[0-9]{4})?$/, // US ZIP
        /^[A-Za-z][0-9][A-Za-z][ ]?[0-9][A-Za-z][0-9]$/, // Canada
        /^[A-Z]{1,2}[0-9][A-Z0-9]? ?[0-9][A-Z]{2}$/i, // UK
        /^[A-Za-z0-9]{3,10}(?:[ -]?[A-Za-z0-9]{1,4})?$/ // Generic international
    ]
        .map((r) => r.source)
        .join('|'),
    'i'
);

export const countryTimezones = {
    Afghanistan: 'Asia/Kabul',
    Albania: 'Europe/Tirane',
    Algeria: 'Africa/Algiers',
    Andorra: 'Europe/Andorra',
    Angola: 'Africa/Luanda',
    'Antigua and Barbuda': 'America/Antigua',
    Argentina: 'America/Argentina/Buenos_Aires',
    Armenia: 'Asia/Yerevan',
    Australia: 'Australia/Sydney',
    Austria: 'Europe/Vienna',
    Azerbaijan: 'Asia/Baku',
    Bahamas: 'America/Nassau',
    Bahrain: 'Asia/Bahrain',
    Bangladesh: 'Asia/Dhaka',
    Barbados: 'America/Barbados',
    Belarus: 'Europe/Minsk',
    Belgium: 'Europe/Brussels',
    Belize: 'America/Belize',
    Benin: 'Africa/Porto-Novo',
    Bhutan: 'Asia/Thimphu',
    Bolivia: 'America/La_Paz',
    'Bosnia and Herzegovina': 'Europe/Sarajevo',
    Botswana: 'Africa/Gaborone',
    Brazil: 'America/Sao_Paulo',
    Brunei: 'Asia/Brunei',
    Bulgaria: 'Europe/Sofia',
    'Burkina Faso': 'Africa/Ouagadougou',
    Burundi: 'Africa/Bujumbura',
    'Cabo Verde': 'Atlantic/Cape_Verde',
    Cambodia: 'Asia/Phnom_Penh',
    Cameroon: 'Africa/Douala',
    Canada: 'America/Toronto',
    'Central African Republic': 'Africa/Bangui',
    Chad: 'Africa/Ndjamena',
    Chile: 'America/Santiago',
    China: 'Asia/Shanghai',
    Colombia: 'America/Bogota',
    Comoros: 'Indian/Comoro',
    Congo: 'Africa/Brazzaville',
    'Costa Rica': 'America/Costa_Rica',
    Croatia: 'Europe/Zagreb',
    Cuba: 'America/Havana',
    Cyprus: 'Asia/Nicosia',
    'Czech Republic': 'Europe/Prague',
    Denmark: 'Europe/Copenhagen',
    Djibouti: 'Africa/Djibouti',
    Dominica: 'America/Dominica',
    'Dominican Republic': 'America/Santo_Domingo',
    Ecuador: 'America/Guayaquil',
    Egypt: 'Africa/Cairo',
    'El Salvador': 'America/El_Salvador',
    'Equatorial Guinea': 'Africa/Malabo',
    Eritrea: 'Africa/Asmara',
    Estonia: 'Europe/Tallinn',
    Eswatini: 'Africa/Mbabane',
    Ethiopia: 'Africa/Addis_Ababa',
    Fiji: 'Pacific/Fiji',
    Finland: 'Europe/Helsinki',
    France: 'Europe/Paris',
    Gabon: 'Africa/Libreville',
    Gambia: 'Africa/Banjul',
    Georgia: 'Asia/Tbilisi',
    Germany: 'Europe/Berlin',
    Ghana: 'Africa/Accra',
    Greece: 'Europe/Athens',
    Grenada: 'America/Grenada',
    Guatemala: 'America/Guatemala',
    Guinea: 'Africa/Conakry',
    'Guinea-Bissau': 'Africa/Bissau',
    Guyana: 'America/Guyana',
    Haiti: 'America/Port-au-Prince',
    Honduras: 'America/Tegucigalpa',
    Hungary: 'Europe/Budapest',
    Iceland: 'Atlantic/Reykjavik',
    India: 'Asia/Kolkata',
    Indonesia: 'Asia/Jakarta',
    Iran: 'Asia/Tehran',
    Iraq: 'Asia/Baghdad',
    Ireland: 'Europe/Dublin',
    Israel: 'Asia/Jerusalem',
    Italy: 'Europe/Rome',
    Jamaica: 'America/Jamaica',
    Japan: 'Asia/Tokyo',
    Jordan: 'Asia/Amman',
    Kazakhstan: 'Asia/Almaty',
    Kenya: 'Africa/Nairobi',
    Kiribati: 'Pacific/Tarawa',
    'Korea, North': 'Asia/Pyongyang',
    'Korea, South': 'Asia/Seoul',
    Kosovo: 'Europe/Belgrade',
    Kuwait: 'Asia/Kuwait',
    Kyrgyzstan: 'Asia/Bishkek',
    Laos: 'Asia/Vientiane',
    Latvia: 'Europe/Riga',
    Lebanon: 'Asia/Beirut',
    Lesotho: 'Africa/Maseru',
    Liberia: 'Africa/Monrovia',
    Libya: 'Africa/Tripoli',
    Liechtenstein: 'Europe/Vaduz',
    Lithuania: 'Europe/Vilnius',
    Luxembourg: 'Europe/Luxembourg',
    Madagascar: 'Indian/Antananarivo',
    Malawi: 'Africa/Blantyre',
    Malaysia: 'Asia/Kuala_Lumpur',
    Maldives: 'Indian/Maldives',
    Mali: 'Africa/Bamako',
    Malta: 'Europe/Malta',
    'Marshall Islands': 'Pacific/Majuro',
    Mauritania: 'Africa/Nouakchott',
    Mauritius: 'Indian/Mauritius',
    Mexico: 'America/Mexico_City',
    Micronesia: 'Pacific/Chuuk',
    Moldova: 'Europe/Chisinau',
    Monaco: 'Europe/Monaco',
    Mongolia: 'Asia/Ulaanbaatar',
    Montenegro: 'Europe/Podgorica',
    Morocco: 'Africa/Casablanca',
    Mozambique: 'Africa/Maputo',
    Myanmar: 'Asia/Yangon',
    Namibia: 'Africa/Windhoek',
    Nauru: 'Pacific/Nauru',
    Nepal: 'Asia/Kathmandu',
    Netherlands: 'Europe/Amsterdam',
    'New Zealand': 'Pacific/Auckland',
    Nicaragua: 'America/Managua',
    Niger: 'Africa/Niamey',
    Nigeria: 'Africa/Lagos',
    'North Macedonia': 'Europe/Skopje',
    Norway: 'Europe/Oslo',
    Oman: 'Asia/Muscat',
    Pakistan: 'Asia/Karachi',
    Palau: 'Pacific/Palau',
    Palestine: 'Asia/Gaza',
    Panama: 'America/Panama',
    'Papua New Guinea': 'Pacific/Port_Moresby',
    Paraguay: 'America/Asuncion',
    Peru: 'America/Lima',
    Philippines: 'Asia/Manila',
    Poland: 'Europe/Warsaw',
    Portugal: 'Europe/Lisbon',
    Qatar: 'Asia/Qatar',
    Romania: 'Europe/Bucharest',
    Russia: 'Europe/Moscow',
    Rwanda: 'Africa/Kigali',
    'Saint Kitts and Nevis': 'America/St_Kitts',
    'Saint Lucia': 'America/St_Lucia',
    'Saint Vincent and the Grenadines': 'America/St_Vincent',
    Samoa: 'Pacific/Apia',
    'San Marino': 'Europe/San_Marino',
    'Sao Tome and Principe': 'Africa/Sao_Tome',
    'Saudi Arabia': 'Asia/Riyadh',
    Senegal: 'Africa/Dakar',
    Serbia: 'Europe/Belgrade',
    Seychelles: 'Indian/Mahe',
    'Sierra Leone': 'Africa/Freetown',
    Singapore: 'Asia/Singapore',
    Slovakia: 'Europe/Bratislava',
    Slovenia: 'Europe/Ljubljana',
    'Solomon Islands': 'Pacific/Guadalcanal',
    Somalia: 'Africa/Mogadishu',
    'South Africa': 'Africa/Johannesburg',
    'South Sudan': 'Africa/Juba',
    Spain: 'Europe/Madrid',
    'Sri Lanka': 'Asia/Colombo',
    Sudan: 'Africa/Khartoum',
    Suriname: 'America/Paramaribo',
    Sweden: 'Europe/Stockholm',
    Switzerland: 'Europe/Zurich',
    Syria: 'Asia/Damascus',
    Taiwan: 'Asia/Taipei',
    Tajikistan: 'Asia/Dushanbe',
    Tanzania: 'Africa/Dar_es_Salaam',
    Thailand: 'Asia/Bangkok',
    'Timor-Leste': 'Asia/Dili',
    Togo: 'Africa/Lome',
    Tonga: 'Pacific/Tongatapu',
    'Trinidad and Tobago': 'America/Port_of_Spain',
    Tunisia: 'Africa/Tunis',
    Turkey: 'Europe/Istanbul',
    Turkmenistan: 'Asia/Ashgabat',
    Tuvalu: 'Pacific/Funafuti',
    Uganda: 'Africa/Kampala',
    Ukraine: 'Europe/Kiev',
    'United Arab Emirates': 'Asia/Dubai',
    'United Kingdom': 'Europe/London',
    'United States': 'America/New_York',
    Uruguay: 'America/Montevideo',
    Uzbekistan: 'Asia/Tashkent',
    Vanuatu: 'Pacific/Efate',
    'Vatican City': 'Europe/Vatican',
    Venezuela: 'America/Caracas',
    Vietnam: 'Asia/Ho_Chi_Minh',
    Yemen: 'Asia/Aden',
    Zambia: 'Africa/Lusaka',
    Zimbabwe: 'Africa/Harare'
};

export const countryPhoneData = {
    Afghanistan: 'af',
    Albania: 'al',
    Algeria: 'dz',
    Andorra: 'ad',
    Angola: 'ao',
    'Antigua and Barbuda': 'ag',
    Argentina: 'ar',
    Armenia: 'am',
    Australia: 'au',
    Austria: 'at',
    Azerbaijan: 'az',
    Bahamas: 'bs',
    Bahrain: 'bh',
    Bangladesh: 'bd',
    Barbados: 'bb',
    Belarus: 'by',
    Belgium: 'be',
    Belize: 'bz',
    Benin: 'bj',
    Bhutan: 'bt',
    Bolivia: 'bo',
    'Bosnia and Herzegovina': 'ba',
    Botswana: 'bw',
    Brazil: 'br',
    Brunei: 'bn',
    Bulgaria: 'bg',
    'Burkina Faso': 'bf',
    Burundi: 'bi',
    'Cabo Verde': 'cv',
    Cambodia: 'kh',
    Cameroon: 'cm',
    Canada: 'ca',
    'Central African Republic': 'cf',
    Chad: 'td',
    Chile: 'cl',
    China: 'cn',
    Colombia: 'co',
    Comoros: 'km',
    Congo: 'cg',
    'Costa Rica': 'cr',
    Croatia: 'hr',
    Cuba: 'cu',
    Cyprus: 'cy',
    'Czech Republic': 'cz',
    Denmark: 'dk',
    Djibouti: 'dj',
    Dominica: 'dm',
    'Dominican Republic': 'do',
    Ecuador: 'ec',
    Egypt: 'eg',
    'El Salvador': 'sv',
    'Equatorial Guinea': 'gq',
    Eritrea: 'er',
    Estonia: 'ee',
    Eswatini: 'sz',
    Ethiopia: 'et',
    Fiji: 'fj',
    Finland: 'fi',
    France: 'fr',
    Gabon: 'ga',
    Gambia: 'gm',
    Georgia: 'ge',
    Germany: 'de',
    Ghana: 'gh',
    Greece: 'gr',
    Grenada: 'gd',
    Guatemala: 'gt',
    Guinea: 'gn',
    'Guinea-Bissau': 'gw',
    Guyana: 'gy',
    Haiti: 'ht',
    Honduras: 'hn',
    Hungary: 'hu',
    Iceland: 'is',
    India: 'in',
    Indonesia: 'id',
    Iran: 'ir',
    Iraq: 'iq',
    Ireland: 'ie',
    Israel: 'il',
    Italy: 'it',
    Jamaica: 'jm',
    Japan: 'jp',
    Jordan: 'jo',
    Kazakhstan: 'kz',
    Kenya: 'ke',
    Kiribati: 'ki',
    'Korea, North': 'kp',
    'Korea, South': 'kr',
    Kosovo: 'xk',
    Kuwait: 'kw',
    Kyrgyzstan: 'kg',
    Laos: 'la',
    Latvia: 'lv',
    Lebanon: 'lb',
    Lesotho: 'ls',
    Liberia: 'lr',
    Libya: 'ly',
    Liechtenstein: 'li',
    Lithuania: 'lt',
    Luxembourg: 'lu',
    Madagascar: 'mg',
    Malawi: 'mw',
    Malaysia: 'my',
    Maldives: 'mv',
    Mali: 'ml',
    Malta: 'mt',
    'Marshall Islands': 'mh',
    Mauritania: 'mr',
    Mauritius: 'mu',
    Mexico: 'mx',
    Micronesia: 'fm',
    Moldova: 'md',
    Monaco: 'mc',
    Mongolia: 'mn',
    Montenegro: 'me',
    Morocco: 'ma',
    Mozambique: 'mz',
    Myanmar: 'mm',
    Namibia: 'na',
    Nauru: 'nr',
    Nepal: 'np',
    Netherlands: 'nl',
    'New Zealand': 'nz',
    Nicaragua: 'ni',
    Niger: 'ne',
    Nigeria: 'ng',
    'North Macedonia': 'mk',
    Norway: 'no',
    Oman: 'om',
    Pakistan: 'pk',
    Palau: 'pw',
    Palestine: 'ps',
    Panama: 'pa',
    'Papua New Guinea': 'pg',
    Paraguay: 'py',
    Peru: 'pe',
    Philippines: 'ph',
    Poland: 'pl',
    Portugal: 'pt',
    Qatar: 'qa',
    Romania: 'ro',
    Russia: 'ru',
    Rwanda: 'rw',
    'Saint Kitts and Nevis': 'kn',
    'Saint Lucia': 'lc',
    'Saint Vincent and the Grenadines': 'vc',
    Samoa: 'ws',
    'San Marino': 'sm',
    'Sao Tome and Principe': 'st',
    'Saudi Arabia': 'sa',
    Senegal: 'sn',
    Serbia: 'rs',
    Seychelles: 'sc',
    'Sierra Leone': 'sl',
    Singapore: 'sg',
    Slovakia: 'sk',
    Slovenia: 'si',
    'Solomon Islands': 'sb',
    Somalia: 'so',
    'South Africa': 'za',
    'South Sudan': 'ss',
    Spain: 'es',
    'Sri Lanka': 'lk',
    Sudan: 'sd',
    Suriname: 'sr',
    Sweden: 'se',
    Switzerland: 'ch',
    Syria: 'sy',
    Taiwan: 'tw',
    Tajikistan: 'tj',
    Tanzania: 'tz',
    Thailand: 'th',
    'Timor-Leste': 'tl',
    Togo: 'tg',
    Tonga: 'to',
    'Trinidad and Tobago': 'tt',
    Tunisia: 'tn',
    Turkey: 'tr',
    Turkmenistan: 'tm',
    Tuvalu: 'tv',
    Uganda: 'ug',
    Ukraine: 'ua',
    'United Arab Emirates': 'ae',
    'United Kingdom': 'gb',
    'United States': 'us',
    Uruguay: 'uy',
    Uzbekistan: 'uz',
    Vanuatu: 'vu',
    'Vatican City': 'va',
    Venezuela: 've',
    Vietnam: 'vn',
    Yemen: 'ye',
    Zambia: 'zm',
    Zimbabwe: 'zw'
};
/**
 * Array of Indian states and union territories with their latitude and longitude coordinates
 *
 * @type {Array<{name: string, value: string, latitude: number, longitude: number}>}
 */
export const indianStates = [
    {
        name: 'Andhra Pradesh',
        value: 'AP',
        latitude: 15.9129,
        longitude: 79.74
    },
    {
        name: 'Arunachal Pradesh',
        value: 'AR',
        latitude: 27.1004,
        longitude: 93.6167
    },
    {
        name: 'Assam',
        value: 'AS',
        latitude: 26.2006,
        longitude: 92.9376
    },
    {
        name: 'Bihar',
        value: 'BR',
        latitude: 25.0961,
        longitude: 85.3131
    },
    {
        name: 'Chhattisgarh',
        value: 'CG',
        latitude: 21.2787,
        longitude: 81.8661
    },
    {
        name: 'Goa',
        value: 'GA',
        latitude: 15.2993,
        longitude: 74.124
    },
    {
        name: 'Gujarat',
        value: 'GJ',
        latitude: 22.2587,
        longitude: 71.1924
    },
    {
        name: 'Haryana',
        value: 'HR',
        latitude: 29.0588,
        longitude: 76.0856
    },
    {
        name: 'Himachal Pradesh',
        value: 'HP',
        latitude: 31.1048,
        longitude: 77.1734
    },
    {
        name: 'Jharkhand',
        value: 'JH',
        latitude: 23.6102,
        longitude: 85.2799
    },
    {
        name: 'Karnataka',
        value: 'KA',
        latitude: 15.3173,
        longitude: 75.7139
    },
    {
        name: 'Kerala',
        value: 'KL',
        latitude: 10.8505,
        longitude: 76.2711
    },
    {
        name: 'Madhya Pradesh',
        value: 'MP',
        latitude: 22.9734,
        longitude: 78.6569
    },
    {
        name: 'Maharashtra',
        value: 'MH',
        latitude: 19.7515,
        longitude: 75.7139
    },
    {
        name: 'Manipur',
        value: 'MN',
        latitude: 24.6637,
        longitude: 93.9063
    },
    {
        name: 'Meghalaya',
        value: 'ML',
        latitude: 25.467,
        longitude: 91.3662
    },
    {
        name: 'Mizoram',
        value: 'MZ',
        latitude: 23.1645,
        longitude: 92.9376
    },
    {
        name: 'Nagaland',
        value: 'NL',
        latitude: 26.1584,
        longitude: 94.5624
    },
    {
        name: 'Odisha',
        value: 'OD',
        latitude: 20.9517,
        longitude: 85.0985
    },
    {
        name: 'Punjab',
        value: 'PB',
        latitude: 31.1471,
        longitude: 75.3412
    },
    {
        name: 'Rajasthan',
        value: 'RJ',
        latitude: 27.0238,
        longitude: 74.2179
    },
    {
        name: 'Sikkim',
        value: 'SK',
        latitude: 27.533,
        longitude: 88.5122
    },
    {
        name: 'Tamil Nadu',
        value: 'TN',
        latitude: 11.1271,
        longitude: 78.6569
    },
    {
        name: 'Telangana',
        value: 'TG',
        latitude: 18.1124,
        longitude: 79.0193
    },
    {
        name: 'Tripura',
        value: 'TR',
        latitude: 23.9408,
        longitude: 91.9882
    },
    {
        name: 'Uttarakhand',
        value: 'UK',
        latitude: 30.0668,
        longitude: 79.0193
    },
    {
        name: 'Uttar Pradesh',
        value: 'UP',
        latitude: 26.8467,
        longitude: 80.9462
    },
    {
        name: 'West Bengal',
        value: 'WB',
        latitude: 22.9868,
        longitude: 87.855
    },
    {
        name: 'Andaman and Nicobar Islands',
        value: 'AN',
        latitude: 11.7401,
        longitude: 92.6586
    },
    {
        name: 'Chandigarh',
        value: 'CH',
        latitude: 30.7333,
        longitude: 76.7794
    },
    {
        name: 'Dadra and Nagar Haveli and Daman and Diu',
        value: 'DN',
        latitude: 20.1809,
        longitude: 73.0169
    },
    {
        name: 'Delhi',
        value: 'DL',
        latitude: 28.7041,
        longitude: 77.1025
    },
    {
        name: 'Jammu and Kashmir',
        value: 'JK',
        latitude: 33.7782,
        longitude: 76.5762
    },
    {
        name: 'Ladakh',
        value: 'LA',
        latitude: 34.2268,
        longitude: 77.5619
    },
    {
        name: 'Lakshadweep',
        value: 'LD',
        latitude: 10.5667,
        longitude: 72.6417
    },
    {
        name: 'Puducherry',
        value: 'PY',
        latitude: 11.9416,
        longitude: 79.8083
    }
];

export const SERVER_ERROR = 'Internal Server Error';

export const formatTimeText = (text) => {
    if (!text) return '';

    // Split by underscore and capitalize each word
    return text
        .split('_')
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join(' ');
};
export function convertTimeTextToFullFormat(timeText) {
    if (!timeText) return '';

    // Extract the period (AM/PM) - last character
    const period = timeText.slice(-1).toLowerCase(); // 'a' for AM, 'p' for PM

    // Extract the time part (everything except the last character)
    const timePart = timeText.slice(0, -1);

    let hour, minutes;

    if (timePart.length <= 2) {
        // Format: "2a", "10p" (hour only, minutes = 00)
        hour = parseInt(timePart, 10);
        minutes = '00';
    } else {
        // Format: "230a", "1045p" (last 2 digits are minutes, rest are hours)
        minutes = timePart.slice(-2);
        hour = parseInt(timePart.slice(0, -2), 10);
    }

    // Convert the hour to the full AM/PM format
    let formattedTime = '';

    if (period === 'a') {
        formattedTime = hour === 12 ? `12:${minutes} AM` : `${hour}:${minutes} AM`;
    } else if (period === 'p') {
        formattedTime = hour === 12 ? `12:${minutes} PM` : `${hour}:${minutes} PM`;
    }

    return formattedTime;
}

export function convertToDateFormat(timeString) {
    // Split the input string into hours and minutes
    const [hours, minutes] = timeString.split(':').map(Number);

    // Create a new Date object (use the current date, but adjust the time)
    const date = new Date();
    date.setHours(hours);
    date.setMinutes(minutes);
    date.setSeconds(0); // Set seconds to 0 for consistency

    // Return the entire Date object
    return date;
}

export const timeRegex = /^(?:[01][0-9]|2[0-3]):([0-5][0-9])$/;
